import classNames from "classnames";
import { CustomFee, Discount, PaymentMethod, CartFeesDetails } from "../../build/activeEntityType";
import { Fragment } from "react";
import X from "../../assets/removeX.svg";

const calculateAmountDue = (total: number, payments?: PaymentMethod[] | null): number => {
    // payment.amount is already a number in minor units
    const totalPayments = payments?.reduce((sum, payment) => sum + (payment.amount || 0), 0) || 0;
    return total - totalPayments;
};

const adjustFontSize = (fontSize: string | undefined): string | undefined => {
    if (!fontSize) return undefined;

    const fontSizeValue = parseInt(fontSize.replace("px", ""), 10);

    if (fontSizeValue === 0 || fontSizeValue === 1 || fontSizeValue === 2) {
        return `${fontSizeValue}px`;
    }

    const adjustedFontSize = Math.max(0, fontSizeValue - 2);
    return `${adjustedFontSize}px`;
};

export interface SimpleCarFooterProps {
    bg: string;
    tax?: number;
    subtotal: number;
    total: number;
    taxIncl?: boolean;
    showTaxes: boolean;
    taxesLabel?: string;
    dividerBg: string;
    discount?: Discount;
    cartFees?: CustomFee[];
    /** When set, fee amount/label are taken from here (fee on discounted base); otherwise derived from cartFees + subtotal/discount */
    calculatedCartFees?: CartFeesDetails[];
    showSubtotal?: boolean;
    subtotalLabel?: string;
    showTotal?: boolean;
    totalLabel?: string;
    showDiscount?: boolean;
    formatCurrency: (amount: number) => string;
    onCartFeeRemove?: (index: number) => void;
    onRemoveDiscount?: () => void;
    payments?: PaymentMethod[] | null;
    balanceLabel?: string;
    showBalance?: boolean;
    cartTotal?: number;
    cartTotalLabel?: string;
    orderTotal?: number;
    orderTotalLabel?: string;
    showOrderTotal?: boolean;
    totalStyle?: React.CSSProperties;
    subtotalStyle?: React.CSSProperties;
    summarySubtextStyle?: React.CSSProperties;
}
const SimpleCartFooter = ({
    tax,
    subtotal,
    total,
    bg,
    dividerBg,
    showTaxes,
    taxesLabel,
    discount,
    // taxIncl,
    totalStyle,
    subtotalStyle,
    showSubtotal,
    subtotalLabel,
    showDiscount,
    showTotal,
    totalLabel,
    formatCurrency,
    onCartFeeRemove,
    onRemoveDiscount,
    payments,
    balanceLabel,
    showBalance,
    cartFees,
    calculatedCartFees,
    cartTotal,
    orderTotalLabel,
    showOrderTotal,
    summarySubtextStyle
}: SimpleCarFooterProps) => {
    if (showTotal || showSubtotal || showDiscount || showTaxes || showOrderTotal || showBalance) {
        const totalClass = classNames("row total state-divider-color", {
            ["no-border"]: !showSubtotal && !(showDiscount && discount) && !(showTaxes && tax)
        });

        const discountAmount =
            discount && discount.value > 0
                ? discount.isPercent
                    ? discount.value * subtotal
                    : Math.min(discount.value, subtotal)
                : 0;
        const subtotalAfterDiscount = subtotal - discountAmount;

        const getFeeDisplayAmount = (fee: CustomFee, index: number): number => {
            if (calculatedCartFees && calculatedCartFees[index] != null) {
                return calculatedCartFees[index].amount;
            }
            return fee.isPercent ? fee.amount * subtotalAfterDiscount : fee.amount;
        };
        const getFeeLabel = (fee: CustomFee, index: number): string => {
            if (calculatedCartFees && calculatedCartFees[index] != null) {
                return calculatedCartFees[index].name || fee.label || `Cart fee ${index}`;
            }
            return fee.label || `Cart fee ${index}`;
        };

        const amountDue = calculateAmountDue(total, payments);
        const emvStyle = subtotalStyle?.fontSize?.toString() || undefined;
        const adjustedFontSize = adjustFontSize(emvStyle);

        return (
            <footer style={{ backgroundColor: bg }}>
                {showSubtotal && (
                    <div className="row">
                        <span style={subtotalStyle}>{subtotalLabel}</span>
                        <span style={subtotalStyle}>{formatCurrency(subtotal)}</span>
                    </div>
                )}

                {showDiscount && (
                    <>
                        {discount && discount.value > 0 && (
                            <div className="space-between">
                                <div className="row">
                                    <span style={summarySubtextStyle}>
                                        {/* Cart discount {discount.isPercent && `(${(discount.value * 100).toFixed(2)}%)`} */}
                                        {discount.label || "Cart Discount"}
                                    </span>
                                    <span style={summarySubtextStyle}>
                                        {"-" +
                                            (discount.isPercent
                                                ? formatCurrency(discount.value * subtotal)
                                                : formatCurrency(discount.value))}
                                    </span>
                                </div>
                                <div className="remove-fee-btn" onClick={() => onRemoveDiscount && onRemoveDiscount()}>
                                    <img src={X} />
                                </div>
                                {/* {discount.label && <div className="row small-text">{discount.label}</div>} */}
                            </div>
                        )}

                        {cartFees &&
                            cartFees?.map((fee, index) => (
                                <Fragment key={index}>
                                    <div className="space-between">
                                        <div className="row">
                                            <span style={summarySubtextStyle}>
                                                {getFeeLabel(fee, index)}
                                            </span>
                                            <span style={summarySubtextStyle}>
                                                {formatCurrency(getFeeDisplayAmount(fee, index))}
                                            </span>
                                        </div>
                                        <div className="remove-fee-btn" onClick={() => onCartFeeRemove && onCartFeeRemove(index)}>
                                            <img src={X} />
                                        </div>
                                        {/* {discount.label && <div className="row small-text">{discount.label}</div>} */}
                                    </div>
                                </Fragment>
                            ))}
                    </>
                )}

                {showTotal && ((discount && discount.value > 0) || cartFees) && (
                    <div className="row">
                        <span style={subtotalStyle}>{totalLabel}</span>
                        <span style={subtotalStyle}>{formatCurrency(cartTotal || 0)}</span>
                    </div>
                )}

                {showTaxes && (
                    <div className="row">
                        <span style={summarySubtextStyle}>{taxesLabel || ""}</span>
                        <span style={summarySubtextStyle}>{formatCurrency(tax || 0)}</span>
                    </div>
                )}

                {showOrderTotal && (
                    <div className={totalClass} style={dividerBg ? { borderColor: dividerBg } : {}}>
                        <span style={totalStyle}>{orderTotalLabel || ""}</span>
                        <span style={totalStyle}>{formatCurrency(total)}</span>
                    </div>
                )}

                {showOrderTotal && payments && (
                    <div className="payment">
                        {payments.length > 0 &&
                            payments.map(payment => (
                                <Fragment key={payment.transactionId}>
                                    <div className="row payments" style={dividerBg ? { borderColor: dividerBg } : {}}>
                                        <span style={subtotalStyle}>
                                            {payment.paymentType.charAt(0).toUpperCase() + payment.paymentType.slice(1)}
                                        </span>
                                        <span style={subtotalStyle}>-{formatCurrency(+payment.amount)}</span>
                                    </div>
                                    {payment.emv && (
                                        <div className="row payments emv">
                                            <span style={subtotalStyle ? { fontSize: adjustedFontSize } : {}}>
                                                {JSON.parse(payment.emv)["Card Number"]}
                                            </span>
                                            <span />
                                        </div>
                                    )}
                                </Fragment>
                            ))}
                    </div>
                )}

                {showBalance && payments && payments.length > 0 && (
                    <div className={totalClass} style={dividerBg ? { borderColor: dividerBg } : {}}>
                        <span style={totalStyle}>{balanceLabel}</span>
                        <span style={totalStyle}>{formatCurrency(amountDue)}</span>
                    </div>
                )}
            </footer>
        );
    } else return null;
};

export default SimpleCartFooter;
